import React__default, { useContext } from 'react';
import { __extends, __rest, __assign } from '../../_virtual/_tslib.js';
import xor from 'lodash/xor';
import { ChatContext } from '../../containers/ChatContext/index.js';
import withChat from '../../containers/Provider/withChat.js';
import Scrolllock from 'react-scrolllock';
import classnames from 'classnames';
import ConversationQuery from '../../containers/ConversationQuery/index.js';
import { StartGuestConversationComponent } from '../../containers/Conversation/StartGuestConversation.generated.js';
import startGuestConversation from '../../utils/startGuestConversation.js';
import Conversation from '../../containers/Conversation/index.js';
import Composer from '../../containers/Composer/index.js';
import UserQuery from '../../containers/UserQuery/index.js';
import ReactDOM from 'react-dom';
import { Transition, animated } from 'react-spring';
import ChatHeader from './ChatHeader/index.js';
import useMedia from '../../hooks/useMedia.js';
import CloseIcon from './close.svg.js';
import ChatIcon from './chat.svg.js';
import styles from './styles.css.js';

var ChatWidget =
/** @class */
function (_super) {
  __extends(ChatWidget, _super);

  function ChatWidget(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {
      display: !!_this.props.hasUnreadMessages ? 'preview' : 'collapsed'
    };
    _this.expand = _this.expand.bind(_this);
    _this.collapse = _this.collapse.bind(_this);
    _this.getApplicationDomNode = _this.getApplicationDomNode.bind(_this);
    _this.addApplicationClassNames = _this.addApplicationClassNames.bind(_this);
    _this.removeApplicationClassNames = _this.removeApplicationClassNames.bind(_this);
    _this.shrinkApplication = _this.shrinkApplication.bind(_this);
    _this.expandApplication = _this.expandApplication.bind(_this);
    _this.getWidgetStateKeyForPersistentStorage = _this.getWidgetStateKeyForPersistentStorage.bind(_this);
    _this.handleNewMessage = _this.handleNewMessage.bind(_this);
    return _this;
  }

  ChatWidget.prototype.componentDidMount = function () {
    var _this = this;

    this.addApplicationClassNames(styles.transform); // Listen for new messages

    if (this.props.conversation) {
      this.props.conversation.on('newMessage', this.handleNewMessage);
    } // If the user navigates, collapse the widget


    if (this.props.history) {
      this.unlisten = this.props.history.listen(function () {
        if (window.matchMedia('(max-width: 40em)').matches) {
          _this.collapse();
        }
      });
    } // Initialize the local display state from the previously persisted display state


    if (this.props.conversation) {
      var persistedDisplayState = window.sessionStorage.getItem(this.getWidgetStateKeyForPersistentStorage(this.props.conversation.conversationId)); // Restore the previous display state of the chat widget for this conversation

      if (persistedDisplayState) {
        this.setState({
          display: persistedDisplayState
        });
      }
    } // Attempt to start the guest conversation. This happens in the <Conversation> component
    //   as well, but that is not rendered until the <ChatWidget> is expanded. Calling it here
    //   initiates the guest conversation without waiting for user interaction. This method is
    //   idempotent, so the subsequent calls once the <Conversation> is mounted are safe.


    if (this.props.guestChatEnabled) {
      startGuestConversation(this.props.startGuestConversation, this.props.conversation, [this.props.userId]);
    }
  };

  ChatWidget.prototype.componentDidUpdate = function (prevProps) {
    // Listen for messages on the conversation when it's available, or when it has changed.
    var conversation = this.props.conversation;
    var prevConversation = prevProps.conversation;

    if (conversation && (!prevConversation || conversation.conversationId !== prevConversation.conversationId)) {
      if (prevConversation) {
        prevConversation.off('newMessage', this.handleNewMessage);
      }

      conversation.on('newMessage', this.handleNewMessage);
    } // Attempt to start the guest conversation. See comment in `componentDidMount`.


    if (prevProps.conversation !== this.props.conversation || prevProps.authenticatedUserId !== this.props.authenticatedUserId || xor([prevProps.userId], [this.props.userId]).length > 0) {
      if (this.props.guestChatEnabled) {
        startGuestConversation(this.props.startGuestConversation, this.props.conversation, [this.props.userId]);
      }
    }
  };

  ChatWidget.prototype.componentWillUnmount = function () {
    this.expandApplication();
    this.removeApplicationClassNames(styles.transform);

    if (this.props.conversation) {
      this.props.conversation.off('newMessage', this.handleNewMessage);
    }

    if (this.unlisten) {
      this.unlisten();
    }

    if (this.props.conversation) {
      window.sessionStorage.setItem(this.getWidgetStateKeyForPersistentStorage(this.props.conversation.conversationId), this.state.display);
    }
  };

  ChatWidget.prototype.handleNewMessage = function () {
    if (this.state.display === 'collapsed') {
      this.setState({
        display: 'preview'
      });
    }
  };

  ChatWidget.prototype.getApplicationDomNode = function () {
    return this.props.getAppRoot && this.props.getAppRoot();
  };

  ChatWidget.prototype.getWidgetStateKeyForPersistentStorage = function (conversationId) {
    return "conversation:" + conversationId + ":user:" + this.props.userId + ":widget-state";
  };
  /**
   * Expand the widget
   */


  ChatWidget.prototype.expand = function () {
    if (this.state.display === 'expanded') {
      return;
    } // When the ChatWidget expands, we shrink the rest of the application


    this.shrinkApplication();

    if (!this.state.windowScollPositionWhenOpened && this.props.isMobile) {
      this.setState({
        windowScollPositionWhenOpened: [window.scrollX, window.scrollY]
      });
      window.scrollTo(0, 0);
    }

    this.setState({
      display: 'expanded'
    });

    if (this.props.conversation) {
      window.sessionStorage.setItem(this.getWidgetStateKeyForPersistentStorage(this.props.conversation.conversationId), 'expanded');
    }
  };
  /**
   * Collapse the widget
   */


  ChatWidget.prototype.collapse = function () {
    // When the ChatWidget collapses, we reset the rest of the application
    this.expandApplication();

    if (this.state.windowScollPositionWhenOpened && this.props.isMobile) {
      var _a = this.state.windowScollPositionWhenOpened,
          x = _a[0],
          y = _a[1];
      window.scrollTo(x, y);
      this.setState({
        windowScollPositionWhenOpened: undefined
      });
    }

    this.setState({
      display: 'collapsed'
    });

    if (this.props.conversation) {
      window.sessionStorage.setItem(this.getWidgetStateKeyForPersistentStorage(this.props.conversation.conversationId), 'collapsed');
    }
  };
  /**
   * Visually "detach" the rest of the application from the browser
   */


  ChatWidget.prototype.shrinkApplication = function () {
    var domNode = this.getApplicationDomNode();

    if (domNode) {
      this.addApplicationClassNames(styles.shrunk);

      if (domNode instanceof HTMLElement) {
        // Calculate the percentage we need to scale the element in order to shrink by 9px in each dimension
        var rect = domNode.getBoundingClientRect();
        var scaleY = 1 - 18 / rect.height;
        var scaleX = 1 - 18 / rect.width; // Add the transform

        domNode.style.transform = "scaleX(" + scaleX + ") scaleY(" + scaleY + ")";
        domNode.style.webkitTransform = "scaleX(" + scaleX + " scaleY(" + scaleY + ")";
      }
    }
  };
  /**
   * Re-attach the rest of the application from the browser
   */


  ChatWidget.prototype.expandApplication = function () {
    var domNode = this.getApplicationDomNode();

    if (domNode) {
      this.removeApplicationClassNames(styles.shrunk);

      if (domNode instanceof HTMLElement) {
        // Reset the scaling transform (TODO: Do we need to restore the previous value?)
        domNode.style.transform = '';
        domNode.style.webkitTransform = '';
      }
    }
  };
  /**
   * Utility function to add className(s) to the application element
   */


  ChatWidget.prototype.addApplicationClassNames = function (className) {
    var _a;

    var domNode = this.getApplicationDomNode();

    if (domNode) {
      (_a = domNode.classList).add.apply(_a, className.split(' '));
    }
  };
  /**
   * Utility function to remove className(s) to the application element
   */


  ChatWidget.prototype.removeApplicationClassNames = function (className) {
    var _a;

    var domNode = this.getApplicationDomNode();

    if (domNode) {
      (_a = domNode.classList).remove.apply(_a, className.split(' '));
    }
  };

  ChatWidget.prototype.render = function () {
    var _a;

    var _this = this;

    var _b = this.props,
        recipients = _b.recipients,
        hasUnreadMessages = _b.hasUnreadMessages,
        conversation = _b.conversation,
        className = _b.className,
        collapsedClassName = _b.collapsedClassName,
        guestChatEnabled = _b.guestChatEnabled,
        isMobile = _b.isMobile;
    /**
     * TODO: Update these docs. Conversation/Composer components will also take optional conversationId's now.
     *
     * The ChatWidget supports a few states:
     *   - Collapsed, without a Conversation present (the user doesn't have a convo, or we are lazy-loading it)
     *   - Collapsed, with a Conversation (the user has a convo, but has dimissed it or has no unreads)
     *   - Expanded, without a Conversation (the user has tried to initiate a convo, but it isn't loaded yet)
     *   - Expanded, with a Conversation (the user is actively messaging/interacting with the ChatWidget)
     */

    var widget = React__default.createElement("div", {
      className: classnames(styles.widget, (_a = {}, _a[styles.expanded] = this.state.display === 'expanded', _a), collapsedClassName && this.state.display === 'collapsed' ? collapsedClassName : undefined, className)
    }, recipients.length > 0 ? React__default.createElement(Transition, {
      native: true,
      from: {
        opacity: 0,
        transform: 'translate3d(0,100%,0)'
      },
      enter: {
        opacity: 1,
        transform: 'translate3d(0,0%,0)'
      },
      leave: {
        opacity: 0,
        transform: 'translate3d(0,100%,0)'
      }
    }, this.state.display === 'expanded' || this.state.display === 'preview' ? function (animatedStyles) {
      var _a;

      return React__default.createElement(React__default.Fragment, null, React__default.createElement(Scrolllock, {
        isActive: isMobile
      }), React__default.createElement(animated.div, {
        style: animatedStyles,
        className: classnames(styles.chat, (_a = {}, _a[styles.halfExpanded] = _this.state.display === 'preview', _a))
      }, React__default.createElement(ChatHeader, {
        recipients: recipients,
        unread: hasUnreadMessages,
        online: _this.props.online,
        minimized: true,
        ctaIcon: CloseIcon,
        ctaText: "Close chat",
        onCtaClick: _this.collapse,
        onClick: _this.state.display === 'preview' ? _this.expand : _this.collapse,
        className: styles.header
      }), React__default.createElement(Conversation, {
        conversationId: conversation && conversation.conversationId,
        recipientIds: recipients.map(function (recipients) {
          return recipients.userId;
        }),
        enableReadReceipts: _this.state.display === 'expanded',
        onClick: _this.state.display === 'collapsed' ? _this.expand : undefined,
        guestChatEnabled: guestChatEnabled,
        bottomBanner: _this.props.bottomBanner,
        ianaTimeZone: _this.props.ianaTimeZone
      }, _this.props.children), !guestChatEnabled && React__default.createElement(Composer, {
        conversationId: conversation && conversation.conversationId,
        recipientIds: recipients.map(function (recipients) {
          return recipients.userId;
        }),
        onConversationStarted: _this.props.onConversationStarted,
        onSendMessage: _this.props.onSendMessage,
        onFocus: _this.expand,
        source: "chathead",
        guestChatEnabled: _this.props.guestChatEnabled
      })));
    } : function (animatedStyles) {
      return React__default.createElement(animated.div, {
        style: animatedStyles,
        className: classnames(styles.snackbar, _this.props.className)
      }, React__default.createElement(ChatHeader, {
        recipients: recipients,
        unread: hasUnreadMessages,
        online: _this.props.online,
        minimized: false,
        ctaIcon: ChatIcon,
        ctaText: "Chat",
        onClick: _this.expand,
        onCtaClick: _this.expand
      }));
    }) : null); // The ChatWidget can optionally be rendered into a ReactPortal to allow for more control over positioning.

    var parentNode = this.props.getParent && this.props.getParent();
    var bodyNode = document.querySelector('body');
    return ReactDOM.createPortal(widget, parentNode || bodyNode);
  };

  ChatWidget.defaultProps = {
    rating: 'none'
  };
  return ChatWidget;
}(React__default.Component);

var ResponsiveChatWidgetWithFirebaseConversation = function ResponsiveChatWidgetWithFirebaseConversation(_a) {
  var conversationId = _a.conversationId,
      userId = _a.userId,
      guestChatEnabled = _a.guestChatEnabled,
      rest = __rest(_a, ["conversationId", "userId", "guestChatEnabled"]);

  var isMobile = useMedia({
    maxWidth: '40em'
  });
  var tracking = useContext(ChatContext).tracking;
  return React__default.createElement(StartGuestConversationComponent, null, function (startGuestConversation) {
    return React__default.createElement(ConversationQuery, {
      conversationId: conversationId,
      recipientIds: guestChatEnabled ? [userId] : undefined
    }, function (_a) {
      var conversation = _a.conversation;
      return React__default.createElement(UserQuery, {
        userIds: [userId]
      }, function (_a) {
        var users = _a.users,
            usersLoading = _a.loading; // If we have a conversation, prefer the "other" users from that conversation over the user prop

        var otherParticipants = conversation && conversation.participants.filter(function (participant) {
          return !participant.isMyself;
        });
        var recipients = otherParticipants && otherParticipants.length ? otherParticipants : users;
        var hasUnreadMessages = conversation && conversation.unreadCount > 0; // The ChatWidget can render if the conversation is loading (it will show the user's info
        //   in the collapsed state, and a spinner in the expanded state), but it can not render
        //   anything without the user data.

        if (usersLoading) {
          return null;
        }

        return React__default.createElement(ChatWidget, __assign({
          recipients: recipients || [],
          hasUnreadMessages: hasUnreadMessages,
          conversation: conversation,
          userId: userId,
          isMobile: isMobile,
          startGuestConversation: startGuestConversation,
          guestChatEnabled: guestChatEnabled,
          tracking: tracking
        }, rest));
      });
    });
  });
};

var index = withChat(ResponsiveChatWidgetWithFirebaseConversation);

export default index;
//# sourceMappingURL=index.js.map
